import React, { useEffect, useMemo, useState, useCallback } from "react";
import Tabs from "../../../components/Tabs/Tabs";
import Residuals from "./residuals";
import Members from "./members";
import Webhook from "./webhook";
import SubOrganizations from "./sub-organizations";
import OverRides from "./overrides";
import Equipments from "./equipments";
import PricingDefaults from "./pricing-defaults";
import Notes from "./Notes";
import Files from "./Files/Files";
import styles from "./styles.module.scss";
import useCommonStore from "../../../stores/common";
import useDomainStore from "../../../stores/domain";
import ButtonIcon from "../../../components/ButtonIcon/ButtonIcon";
import PlaceholderLogoIcon from "../../../assets/svg/icons/common/placeholder-logo";
import Avatar from "../../../components/Avatar/Avatar";
import PhoneIcon from "../../../assets/svg/icons/common/phone";
import MailIcon from "../../../assets/svg/icons/common/mail";
import LocationPinIcon from "../../../assets/svg/icons/common/location-pin";
import StatusDot from "../../../components/StatusDot/StatusDot";
import DropDown from "../../../components/DropDown/DropDown";
import ExpandDetailsIcon from "../../../assets/svg/icons/common/ExpandDetailsIcon";
import CollapseDetailsIcon from "../../../assets/svg/icons/common/CollapseDetailsIcon";
import CloseIcon24 from "../../../assets/svg/icons/common/CloseIcon24";
import KebabDots16 from "../../../assets/svg/icons/common/KebabDots16";
import toast from "react-hot-toast";
import { useMediaQuery } from "../../../hooks/useMediaQuery";
import { dateFormatter } from "../../../../components/helper/helper";

import {
  GetAdminUsers,
  getDepartmentsList,
  GetOrganizationById,
  getZohoList,
} from "../../../../api";
import useOrganizationStore from "../../../stores/organization";
import useUserStore from "../../../stores/user";
import Spinner from "../../../components/Loaders/Spinner";
import useSubOrganizations from "./sub-organizations/hooks/useSubOrganizations";
import config from "../../../../config";
import OrganizationDetailsLoader from "./details-skeleton-loader";

const MainTabContent = () => {
  const selectedOrganizationData = useOrganizationStore(
    (state) => state.selectedOrganizationData,
  );
  const mainAdminId = selectedOrganizationData?.organization?.main_admin;
  const users = selectedOrganizationData?.users;
  const adminUsers = useOrganizationStore((state) => state.adminUsers);
  const primaryContactId = selectedOrganizationData?.organization?.primary_contact_id;

  const mainAdmin = users?.find((user) => user.id === mainAdminId);
  const teamLead = adminUsers?.find((user) => user.id === primaryContactId);

  return (
    <div className={styles.mainTabContent}>
      <ul>
        <li>
          <span className={styles.listHeadTitle}>Organization ID</span>
          <span className={styles.listHeadValue}>
            {selectedOrganizationData?.organization?.id}
          </span>
        </li>
        <li>
          <PhoneIcon /> <span className={styles.contactLineText}>{selectedOrganizationData?.organization?.phone}</span>
        </li>
        <li>
          <MailIcon /> <span className={styles.contactLineText}>{selectedOrganizationData?.organization?.email}</span>
        </li>
        <li>
          <LocationPinIcon /> <span className={styles.contactLineText}>{selectedOrganizationData?.organization?.address}</span>
        </li>
      </ul>

      {mainAdmin && (
        <ul>
          <li>
            <span className={styles.listHeadTitle}>Admin</span>
            <span className={styles.listHeadValue}>
              <Avatar size="xs" name={mainAdmin?.name?.charAt(0)} />{" "}
              {mainAdmin?.name}
            </span>
          </li>
          <li>
            <PhoneIcon /> <span className={styles.contactLineText}>{mainAdmin.phone_direct}</span>
          </li>
          <li>
            <MailIcon /> <span className={styles.contactLineText}>{mainAdmin.email}</span>
          </li>
        </ul>
      )}

      {teamLead && (
        <ul>
          <li>
            <span className={styles.listHeadTitle}>Team Lead</span>
            <span className={styles.listHeadValue}>
              <Avatar size="xs" name={teamLead?.name?.charAt(0)} /> {teamLead?.name}
            </span>
          </li>
          <li>
            <PhoneIcon /> <span className={styles.contactLineText}>{teamLead.phone_direct}</span>
          </li>
          <li>
            <MailIcon /> <span className={styles.contactLineText}>{teamLead.email}</span>
          </li>
        </ul>
      )}
    </div>
  );
};

const MainAdmin = () => {
  const mainAdminId = useOrganizationStore(
    (state) => state?.selectedOrganizationData?.organization?.main_admin,
  );
  const users = useOrganizationStore(
    (state) => state?.selectedOrganizationData?.users,
  );

  if (!mainAdminId) {
    return null;
  }

  const mainAdmin = users?.find((user) => user.id === mainAdminId);

  if (!mainAdmin) {
    return null;
  }

  return (
    <ul>
      <li>
        <span className={styles.listHeadTitle}>Admin</span>
        <span className={styles.listHeadValue}>
          <Avatar size="xs" name={mainAdmin?.name?.charAt(0)} />{" "}
          {mainAdmin?.name}
        </span>
      </li>
      <li>
        <PhoneIcon /> {mainAdmin.phone_direct}
      </li>
      <li>
        <MailIcon /> {mainAdmin.email}
      </li>
    </ul>
  );
};

const TeamLead = () => {
  const primaryContactId = useOrganizationStore(
    (state) => state.selectedOrganizationData?.organization?.primary_contact_id,
  );
  const adminUsers = useOrganizationStore((state) => state.adminUsers);

  if (!primaryContactId) {
    return null;
  }

  if (!adminUsers) {
    return null;
  }

  const teamLead = adminUsers?.find((user) => user.id === primaryContactId);

  if (!teamLead) {
    return null;
  }

  return (
    <ul>
      <li>
        <span className={styles.listHeadTitle}>Team Lead</span>
        <span className={styles.listHeadValue}>
          <Avatar size="xs" name={teamLead?.name?.charAt(0)} /> {teamLead?.name}
        </span>
      </li>
      <li>
        <PhoneIcon /> {teamLead.phone_direct}
      </li>
      <li>
        <MailIcon /> {teamLead.email}
      </li>
    </ul>
  );
};

const mapOrganizationOfficesToOptions = (offices) => {
  if (!Array.isArray(offices)) {
    return [];
  }

  return offices
    .map((office) => {
      if (typeof office === "string" || typeof office === "number") {
        const value = String(office);
        return { label: value, value };
      }

      if (office && typeof office === "object") {
        const label = office.label ?? office.value;
        const value = office.value ?? office.label;

        if (label && value) {
          return {
            label: String(label),
            value: String(value),
          };
        }
      }

      return null;
    })
    .filter(Boolean);
};

const Details = ({ isDetailsOnly = false }) => {
  const { isKurvDomain } = useDomainStore();
  const isMobile = useMediaQuery(768);
  const [activeIndex, setActiveIndex] = useState(0);
  const isPCAdmin = useUserStore((state) => state.isPCAdmin);
  const isEntitySuperAdmin = useUserStore((state) => state.isEntitySuperAdmin);
  const isAdmin = useUserStore((state) => state.isAdmin);
  const [src, setSrc] = useState("/img/organization/logo-solid.png");
  const [loading, setLoading] = useState(true);
  const expandDetails = useCommonStore((state) => state.expandDetails);
  const toggleExpandDetails = useCommonStore(
    (state) => state.toggleExpandDetails,
  );
  const setEMSOffices = useOrganizationStore((state) => state.setEMSOffices);
  const setAdminUsers = useOrganizationStore((state) => state.setAdminUsers);
  const setZohoUsers = useOrganizationStore((state) => state.setZohoUsers);
  const setDepartments = useOrganizationStore((state) => state.setDepartments);
  const selectedOrganizationId = useOrganizationStore(
    (state) => state.selectedOrganizationId,
  );
  const selectedOrganizationData = useOrganizationStore(
    (state) => state.selectedOrganizationData,
  );
  const setSelectedOrganizationData = useOrganizationStore(
    (state) => state.setSelectedOrganizationData,
  );

  const toggleShowDetails = useCommonStore((state) => state.toggleShowDetails);

  const setEditOrganization = useOrganizationStore(
    (state) => state?.setEditOrganization,
  );
  const setAddOrganizationModalOpen = useOrganizationStore(
    (state) => state.setAddOrganizationModalOpen,
  );
  const setWhitelabelModalOpen = useOrganizationStore(
    (state) => state.setWhitelabelModalOpen,
  );

  const isKurvAdmin = useUserStore((state) => state.isKurvUser) && isAdmin;

  const drop_down_options = [
    {
      text: "Edit Organization",
      type: "normal",
      onClick: () => {
        setEditOrganization(true);
        setAddOrganizationModalOpen(true);
      },
    },
    ...(isEntitySuperAdmin
      ? [
          {
            text: "Whitelabel settings",
            type: "normal",
            onClick: () => setWhitelabelModalOpen(true),
          },
        ]
      : []),
  ];

  const onCloseClick = () => {
    toggleShowDetails();

    if (expandDetails) {
      toggleExpandDetails();
    }
  };

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const response = await GetOrganizationById(selectedOrganizationId);
      if (!response?.success) {
        console.error("Error fetching organization details");
        console.error(response);
        setLoading(false);
        toast.error("Failed to load organization details", { duration: 5000 });
        return;
      }

      setSrc(
        `${config.paymentcloud.STRATUS_URL.replace(/\/$/, "")}/img/${response?.organization?.whitelabel_slug}/logo-solid.png`,
      );
      setSelectedOrganizationData(response);
      setEMSOffices(mapOrganizationOfficesToOptions(response?.organization?.offices));
      setLoading(false);
    } catch (error) {
      console.error(
        "An error occurred while fetching organization details",
        error,
      );
      setLoading(false);
      toast.error("Failed to load organization details", { duration: 5000 });
    }
  }, [selectedOrganizationId, setSelectedOrganizationData, setEMSOffices]);

  const fetchAdminUsers = useCallback(async () => {
    try {
      const response = await GetAdminUsers();

      if (!response?.success) {
        console.error("Error fetching admin users");
        console.error(response);
        toast.error("Failed to load admin users", { duration: 5000 });
        return;
      }
      
      if(response?.kurv_users?.length > 0) {
        setAdminUsers([...response?.users, ...response?.kurv_users]);
      } else {
        setAdminUsers(response?.users);
      }

    } catch (error) {
      console.error("An error occurred while fetching admin users", error);
      toast.error("Failed to load admin users", { duration: 5000 });
    }
  }, [selectedOrganizationId]);

  const fetchZohoUsers = useCallback(async () => {
    try {
      const response = await getZohoList();
      const list = response?.success && response?.zoho_list
        ? response.zoho_list.map((user) => ({
            label: user.name,
            value: user.id,
          }))
        : [];
      setZohoUsers(list);
    } catch (error) {
      console.error("An error occurred while fetching Zoho users", error);
      setZohoUsers([]);
    }
  }, [setZohoUsers]);

  const fetchDepartmentList = useCallback(async () => {
    if (!isPCAdmin) {
      setDepartments([]);
      return;
    }
    try {
      const response = await getDepartmentsList();

      if (!response?.status) {
        setDepartments([]);
        return;
      }

      setDepartments(
        response?.department_list?.map((dept) => ({
          label: dept.name,
          value: dept.id,
        })) ?? [],
      );
    } catch (error) {
      console.error("An error occurred while fetching department list", error);
      toast.error("Failed to load department list", { duration: 5000 });
    }
  }, [setDepartments, isPCAdmin]);

  const canViewOverrideAndEquipmentTabs = isPCAdmin || isKurvAdmin;

  const tabs = useMemo(
    () => [
      {
        label: "Main",
        content: <MainTabContent />,
        badge: null,
        mobileOnly: true,
      },
      {
        label: "Residuals",
        content: <Residuals />,
        badge: null,
      },
      {
        label: "Members",
        content: <Members fetchData={fetchData} />,
        badge: String(selectedOrganizationData?.users?.length),
        badgeStyle: "info",
      },
      {
        label: "Webhook",
        content: <Webhook />,
        badge: null,
        // Webhook config decides where an application's SSN, EIN and bank
        // details get POSTed, so WebhookController requires role === 'admin'
        // on save, log-read and secret rotation alike. Without this the tab
        // rendered for agents and then 403'd on every action inside it.
        hidden: !isAdmin,
      },
      {
        label: "Sub Organizations",
        content: <SubOrganizations />,
        badge: null,
      },
      {
        label: "Overrides",
        content: <OverRides />,
        badge: null,
        hidden: !canViewOverrideAndEquipmentTabs,
      },
      {
        label: "Equipments",
        content: <Equipments />,
        badge: null,
        hidden: !canViewOverrideAndEquipmentTabs,
      },
      {
        label: "Pricing Defaults",
        content: <PricingDefaults />,
        badge: null,
        hidden: !isPCAdmin,
      },
      {
        label: "Notes",
        content: <Notes />,
        badge: String(selectedOrganizationData?.notes?.length),
        badgeStyle: "normal",
        hidden: !isPCAdmin,
      },
      {
        label: "Files",
        content: <Files />,
        badge: String(selectedOrganizationData?.files_count),
        badgeStyle: "normal",
        hidden: !isPCAdmin,
      },
    ],
    [selectedOrganizationData, isPCAdmin, isAdmin, canViewOverrideAndEquipmentTabs],
  );

  // ToDo: implement abort controller or not set data if unmounted
  useEffect(() => {
    if (!selectedOrganizationId) return;
    
    fetchData();
    fetchAdminUsers();
    fetchZohoUsers();
    fetchDepartmentList();
  }, [selectedOrganizationId, fetchData, fetchAdminUsers, fetchZohoUsers, fetchDepartmentList]);

  if (loading) {
    return (
      <div className={styles.details}>
        <OrganizationDetailsLoader />
      </div>
    );
  }

  return (
    <>
    <div className={styles.details}>
      {!isDetailsOnly && (
      <div className={styles.details_head}>
        <button
          className={styles.expand_button}
          onClick={() => {
            toggleExpandDetails();
          }}
        >
          {!expandDetails ? (
            <ExpandDetailsIcon fill={isKurvDomain ? "#22A276" : "#254FE2"} />
          ) : (
            <CollapseDetailsIcon fill={isKurvDomain ? "#22A276" : "#254FE2"} />
          )}

          <span style={{ color: isKurvDomain ? "#22A276" : "#254FE2" }}>{!expandDetails ? "Expand Details" : "Minimize"}</span>
        </button>

        <ButtonIcon onClick={onCloseClick}>
          <CloseIcon24 />
        </ButtonIcon>
      </div>
      )}

      <div className={styles.organizationInfo}>
        <div className={styles.orgInfoTop}>
          <div className={styles.orgLogoArea}>
            <div className={styles.orgLogoImageContainer}>
              {src ? (
                <img src={src} alt="Organization Logo" onError={() => setSrc("")} />
              ) : (
                <PlaceholderLogoIcon />
              )}
              <div className={styles.shadow}></div>
            </div>
            <div className={styles.orgNameContainer}>
              <p className={styles.orgName}>
                {selectedOrganizationData?.organization?.name}
              </p>
              <p className={styles.orgOpenSince}>
                <StatusDot color="#37B450" />{" "}
                <span>
                  Open since{" "}
                  {selectedOrganizationData?.organization?.open_since
                    ? dateFormatter(
                        selectedOrganizationData.organization.open_since,
                      )
                    : "-"}
                </span>
              </p>
            </div>
          </div>

          <div className={styles.orgActions}>
            <DropDown options={drop_down_options}>
              <KebabDots16 />
            </DropDown>
          </div>
        </div>

        <div
          className={`${styles.orgContactInfo} ${expandDetails ? styles.expanded : ""}`}
        >
          <ul>
            {!isKurvDomain ? (
              <li>
                <span className={styles.listHeadTitle}>Organization ID</span>
                <span className={styles.listHeadValue}>
                  {selectedOrganizationData?.organization?.id}
                </span>
              </li>
            ) : null}
            <li>
              <PhoneIcon /> {selectedOrganizationData?.organization?.phone}
            </li>
            <li>
              <MailIcon /> {selectedOrganizationData?.organization?.email}
            </li>
            <li>
              <LocationPinIcon />{" "}
              {selectedOrganizationData?.organization?.address}
            </li>
          </ul>

          {expandDetails && (
            <>
              <MainAdmin />
              <TeamLead />
            </>
          )}
        </div>
      </div>

      <Tabs tabs={tabs} active_index={isMobile ? 0 : 1} isMobile />
    </div>
    </>
  );
};

export default Details;
